import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Check, GraduationCap, Loader2, KeyRound, FileText, CreditCard } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { searchEcoles, type EtablissementSummary } from "@/lib/api";
import { useClaimByCode, useCreateDemande, useClaimElevePaiement } from "@/lib/backoffice-hooks";
import { friendlyError } from "@/lib/errors";

export interface AddChildDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Petit hook de debounce local pour la recherche d'ecole. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const EMPTY_FORM = {
  nom: "",
  prenom: "",
  dateNaissance: "",
  sexe: "" as "" | "M" | "F",
  lieuNaissance: "",
  matriculeConnu: "",
  lien: "",
};

type Mode = "code" | "demande" | "paiement";

export default function AddChildDialog({ open, onOpenChange }: AddChildDialogProps) {
  const [mode, setMode] = useState<Mode>("code");

  // --- Voie B : code de rattachement ---
  const [code, setCode] = useState("");
  const claimByCode = useClaimByCode();

  // --- Voie A : token de paiement déjà effectué (parent authentifié) ---
  const [token, setToken] = useState("");
  const claimElevePaiement = useClaimElevePaiement();

  // --- Voie C : demande (école + identité) ---
  const [ecole, setEcole] = useState<EtablissementSummary | null>(null);
  const [ecoleQuery, setEcoleQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const createDemande = useCreateDemande();

  const debouncedQuery = useDebounced(ecoleQuery, 300);

  // Reinitialise tout a chaque ouverture.
  useEffect(() => {
    if (open) {
      setMode("code");
      setCode("");
      setToken("");
      setEcole(null);
      setEcoleQuery("");
      setShowResults(false);
      setForm(EMPTY_FORM);
      setTouched(false);
    }
  }, [open]);

  const search = useQuery({
    queryKey: ["ecoles-autocomplete", debouncedQuery],
    queryFn: () => searchEcoles({ q: debouncedQuery, size: 8 }),
    enabled: open && mode === "demande" && (debouncedQuery ?? "").trim().length >= 2 && !ecole,
  });

  const results = search.data?.items ?? [];

  function update<K extends keyof typeof EMPTY_FORM>(key: K, value: (typeof EMPTY_FORM)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function selectEcole(e: EtablissementSummary) {
    setEcole(e);
    setEcoleQuery(e.denomination ?? "");
    setShowResults(false);
  }

  function resetEcole() {
    setEcole(null);
    setEcoleQuery("");
    setShowResults(true);
  }

  // ---- Voie B : code ----
  function submitCode(ev: React.FormEvent) {
    ev.preventDefault();
    const normalized = code.trim().toUpperCase();
    if (!normalized) {
      toast.error("Veuillez saisir le code de rattachement.");
      return;
    }
    claimByCode.mutate(normalized, {
      onSuccess: () => {
        toast.success("Enfant rattaché avec succès.");
        onOpenChange(false);
      },
      onError: (err: unknown) => toast.error(friendlyError(err)),
    });
  }

  // ---- Voie A : token de paiement (parent déjà connecté) ----
  function submitPaiement(ev: React.FormEvent) {
    ev.preventDefault();
    const tk = token.trim();
    if (!tk) {
      toast.error("Veuillez saisir la référence du paiement.");
      return;
    }
    claimElevePaiement.mutate(tk, {
      onSuccess: () => {
        toast.success("Paiement rattaché à votre compte.");
        onOpenChange(false);
      },
      onError: (err: unknown) => toast.error(friendlyError(err)),
    });
  }

  // ---- Voie C : demande ----
  const demandeErrors = {
    ecole: !ecole ? "Veuillez sélectionner une école." : "",
    nom: !form.nom.trim() ? "Le nom est requis." : "",
    prenom: !form.prenom.trim() ? "Les prénoms sont requis." : "",
    dateNaissance: !form.dateNaissance ? "La date de naissance est requise." : "",
  };
  const demandeHasErrors = Object.values(demandeErrors).some(Boolean);

  function submitDemande(ev: React.FormEvent) {
    ev.preventDefault();
    setTouched(true);
    if (demandeHasErrors || !ecole) {
      toast.error("Veuillez compléter les champs requis.");
      return;
    }
    createDemande.mutate(
      {
        ecoleId: ecole.id,
        nom: form.nom.trim(),
        prenom: form.prenom.trim(),
        dateNaissance: form.dateNaissance,
        sexe: form.sexe || undefined,
        lieuNaissance: form.lieuNaissance.trim() || undefined,
        matriculeConnu: form.matriculeConnu.trim() || undefined,
        lien: form.lien.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Demande envoyée, en attente de validation de l'école.");
          onOpenChange(false);
        },
        onError: (err: unknown) => toast.error(friendlyError(err)),
      },
    );
  }

  // ---- Sélecteur d'école (mode demande) ----
  const ecolePicker = (
    <div className="relative">
      <Label htmlFor="add-child-ecole">École *</Label>
      {ecole ? (
        <div className="mt-1 flex items-center justify-between gap-3 rounded-xl border border-navy/15 bg-cream px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <GraduationCap size={18} className="shrink-0 text-navy" />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-navy">{ecole.denomination}</p>
              {ecole.communeNom && (
                <p className="truncate text-xs text-navy/60">{ecole.communeNom}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={resetEcole}
            className="shrink-0 text-xs font-bold text-coral hover:underline"
          >
            Changer
          </button>
        </div>
      ) : (
        <div className="relative mt-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-navy/40"
          />
          <Input
            id="add-child-ecole"
            className="pl-9"
            placeholder="Rechercher une école…"
            value={ecoleQuery}
            autoComplete="off"
            aria-invalid={touched && !!demandeErrors.ecole}
            onChange={(e) => {
              setEcoleQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
          />
          {showResults && debouncedQuery.trim().length >= 2 && (
            <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-navy/15 bg-white shadow-lg">
              {search.isLoading ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-navy/60">
                  <Loader2 size={16} className="animate-spin" /> Recherche...
                </div>
              ) : results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-navy/60">Aucune école trouvée.</div>
              ) : (
                results.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => selectEcole(r)}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-cream"
                  >
                    <GraduationCap size={16} className="shrink-0 text-navy/50" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-navy">
                        {r.denomination}
                      </span>
                      {r.communeNom && (
                        <span className="block truncate text-xs text-navy/55">{r.communeNom}</span>
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
      {touched && demandeErrors.ecole && (
        <p className="mt-1 text-xs text-red-600">{demandeErrors.ecole}</p>
      )}
    </div>
  );

  const cancelBtn = (
    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
      Annuler
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden p-0">
        {/* Entete navy */}
        <div className="shrink-0 bg-navy px-6 py-5 text-white">
          <DialogTitle className="font-display text-xl font-bold text-white">
            Ajouter un enfant
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm text-white/70">
            Rattachez un enfant à votre compte pour suivre sa scolarité et ses paiements.
          </DialogDescription>
        </div>

        <div className="overflow-y-auto px-6 pb-6 pt-4">
          <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger
                value="code"
                className="data-[state=active]:bg-navy data-[state=active]:text-white"
              >
                <KeyRound size={15} className="mr-1.5" /> Code
              </TabsTrigger>
              <TabsTrigger
                value="paiement"
                className="data-[state=active]:bg-navy data-[state=active]:text-white"
              >
                <CreditCard size={15} className="mr-1.5" /> Paiement
              </TabsTrigger>
              <TabsTrigger
                value="demande"
                className="data-[state=active]:bg-navy data-[state=active]:text-white"
              >
                <FileText size={15} className="mr-1.5" /> Demande
              </TabsTrigger>
            </TabsList>

            {/* Onglet 1 — Code de rattachement (Voie B) */}
            <TabsContent value="code" className="mt-4">
              <form onSubmit={submitCode} noValidate className="space-y-4">
                <p className="text-sm text-navy/70">
                  Saisissez le code de rattachement remis par l'école. Le lien est immédiat.
                </p>
                <div>
                  <Label htmlFor="add-child-code">Code de rattachement *</Label>
                  <Input
                    id="add-child-code"
                    className="font-mono uppercase"
                    placeholder="ABCD2345"
                    value={code}
                    autoComplete="off"
                    onChange={(e) => setCode(e.target.value)}
                  />
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  {cancelBtn}
                  <Button
                    type="submit"
                    className="bg-navy text-white hover:bg-navy/90"
                    disabled={claimByCode.isPending}
                  >
                    {claimByCode.isPending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Rattachement...
                      </>
                    ) : (
                      <>
                        <Check size={16} /> Rattacher
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* Onglet 2 — Paiement déjà effectué (Voie A) */}
            <TabsContent value="paiement" className="mt-4">
              <form onSubmit={submitPaiement} noValidate className="space-y-4">
                <p className="text-sm text-navy/70">
                  Vous avez déjà payé en ligne pour cet enfant ? Saisissez la référence du paiement
                  pour rattacher l'élève à votre compte.
                </p>
                <div>
                  <Label htmlFor="add-child-token">Référence du paiement *</Label>
                  <Input
                    id="add-child-token"
                    className="font-mono"
                    placeholder="PAY-2026-..."
                    value={token}
                    autoComplete="off"
                    onChange={(e) => setToken(e.target.value)}
                  />
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  {cancelBtn}
                  <Button
                    type="submit"
                    className="bg-navy text-white hover:bg-navy/90"
                    disabled={claimElevePaiement.isPending}
                  >
                    {claimElevePaiement.isPending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Rattachement...
                      </>
                    ) : (
                      <>
                        <Check size={16} /> Rattacher le paiement
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* Onglet 3 — Demande de rattachement (Voie C) */}
            <TabsContent value="demande" className="mt-4">
              <form onSubmit={submitDemande} noValidate className="space-y-4">
                <p className="text-sm text-navy/70">
                  Déposez une demande : elle sera validée par l'école avant le rattachement.
                </p>
                {ecolePicker}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="add-child-nom">Nom *</Label>
                    <Input
                      id="add-child-nom"
                      value={form.nom}
                      aria-invalid={touched && !!demandeErrors.nom}
                      onChange={(e) => update("nom", e.target.value)}
                    />
                    {touched && demandeErrors.nom && (
                      <p className="mt-1 text-xs text-red-600">{demandeErrors.nom}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="add-child-prenom">Prénoms *</Label>
                    <Input
                      id="add-child-prenom"
                      value={form.prenom}
                      aria-invalid={touched && !!demandeErrors.prenom}
                      onChange={(e) => update("prenom", e.target.value)}
                    />
                    {touched && demandeErrors.prenom && (
                      <p className="mt-1 text-xs text-red-600">{demandeErrors.prenom}</p>
                    )}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="add-child-naissance">Date de naissance *</Label>
                    <Input
                      id="add-child-naissance"
                      type="date"
                      value={form.dateNaissance}
                      aria-invalid={touched && !!demandeErrors.dateNaissance}
                      onChange={(e) => update("dateNaissance", e.target.value)}
                    />
                    {touched && demandeErrors.dateNaissance && (
                      <p className="mt-1 text-xs text-red-600">{demandeErrors.dateNaissance}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="add-child-sexe">Sexe</Label>
                    <select
                      id="add-child-sexe"
                      value={form.sexe}
                      onChange={(e) => update("sexe", e.target.value as "" | "M" | "F")}
                      className="w-full rounded-xl border border-navy/15 bg-white px-4 py-2.5 text-navy focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/20"
                    >
                      <option value="">Non précisé</option>
                      <option value="M">Masculin</option>
                      <option value="F">Féminin</option>
                    </select>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="add-child-lieu">Lieu de naissance</Label>
                    <Input
                      id="add-child-lieu"
                      value={form.lieuNaissance}
                      onChange={(e) => update("lieuNaissance", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="add-child-lien">Lien de parenté</Label>
                    <Input
                      id="add-child-lien"
                      placeholder="Ex. Père, Mère, Tuteur…"
                      value={form.lien}
                      onChange={(e) => update("lien", e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="add-child-matricule">Matricule (si connu)</Label>
                  <Input
                    id="add-child-matricule"
                    placeholder="Ex. MAT-2026-..."
                    value={form.matriculeConnu}
                    autoComplete="off"
                    onChange={(e) => update("matriculeConnu", e.target.value)}
                  />
                  <p className="mt-1 text-xs text-navy/55">
                    Facilite le rapprochement avec le dossier de l'école.
                  </p>
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  {cancelBtn}
                  <Button
                    type="submit"
                    className="bg-navy text-white hover:bg-navy/90"
                    disabled={createDemande.isPending}
                  >
                    {createDemande.isPending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Envoi...
                      </>
                    ) : (
                      <>
                        <Check size={16} /> Envoyer la demande
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
